export interface MessageTimestampDisplay {
  shortLabel: string
  tooltipLabel: string
}

const INVALID_TIMESTAMP_DISPLAY: MessageTimestampDisplay = {
  shortLabel: '—',
  tooltipLabel: 'Invalid timestamp',
}

export interface MessageTimestampOptions {
  locale?: string | string[]
  now?: Date
  timeZone?: string
}

function getDateParts(date: Date, locale?: string | string[], timeZone?: string): {
  year: string
  month: string
  day: string
} {
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date)

  const lookup = new Map(parts.map(part => [part.type, part.value]))

  return {
    year: lookup.get('year') || '',
    month: lookup.get('month') || '',
    day: lookup.get('day') || '',
  }
}

function isSameLocalDay(a: Date, b: Date, locale?: string | string[], timeZone?: string): boolean {
  const first = getDateParts(a, locale, timeZone)
  const second = getDateParts(b, locale, timeZone)

  return first.year === second.year && first.month === second.month && first.day === second.day
}

function isSameLocalYear(a: Date, b: Date, locale?: string | string[], timeZone?: string): boolean {
  return getDateParts(a, locale, timeZone).year === getDateParts(b, locale, timeZone).year
}

export function formatMessageTimestamp(
  timestamp: number,
  {
    locale,
    now = new Date(),
    timeZone,
  }: MessageTimestampOptions = {}
): MessageTimestampDisplay {
  if (!Number.isFinite(timestamp)) {
    return INVALID_TIMESTAMP_DISPLAY
  }

  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) {
    return INVALID_TIMESTAMP_DISPLAY
  }

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  })

  const sameDay = isSameLocalDay(date, now, locale, timeZone)
  const sameYear = isSameLocalYear(date, now, locale, timeZone)

  const shortLabel = sameDay
    ? timeFormatter.format(date)
    : sameYear
      ? `${new Intl.DateTimeFormat(locale, {
          timeZone,
          month: 'short',
          day: 'numeric',
        }).format(date)}, ${timeFormatter.format(date)}`
      : `${new Intl.DateTimeFormat(locale, {
          timeZone,
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }).format(date)}, ${timeFormatter.format(date)}`

  const tooltipLabel = new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)

  return { shortLabel, tooltipLabel }
}
